import { useState, useEffect, useCallback } from 'react';
import { 
  getAllTickets, 
  getTicketDetails, 
  respondToTicket, 
  updateTicketStatus, 
  assignTicket,
  getDashboardStats,
  getUnassignedTickets,
  getMyAssignedTickets
} from '../services/supportService';

export const useSupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Fetch tickets with filters
  const fetchTickets = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAllTickets(params);
      setTickets(response.tickets || []);
      setPagination({
        page: response.page || 1,
        limit: response.limit || 10,
        total: response.total || 0,
        totalPages: response.totalPages || 0
      });
    } catch (err) {
      setError(err.message);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get ticket details
  const fetchTicketDetails = useCallback(async (ticketId) => {
    try {
      const response = await getTicketDetails(ticketId);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Respond to ticket
  const respondToTicketAction = useCallback(async (ticketId, message, isInternal = false) => {
    try {
      const response = await respondToTicket(ticketId, { message, isInternal });
      // Refresh tickets to show updated data
      await fetchTickets();
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [fetchTickets]);

  // Update ticket status
  const updateTicketStatusAction = useCallback(async (ticketId, status) => {
    try {
      const response = await updateTicketStatus(ticketId, status);
      // Update local state
      setTickets(prevTickets => 
        prevTickets.map(ticket => 
          ticket.ticketId === ticketId 
            ? { ...ticket, status, lastActivity: new Date().toISOString() }
            : ticket
        )
      );
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Assign ticket
  const assignTicketAction = useCallback(async (ticketId, assignedTo) => {
    try {
      const response = await assignTicket(ticketId, assignedTo);
      // Update local state
      setTickets(prevTickets => 
        prevTickets.map(ticket => 
          ticket.ticketId === ticketId 
            ? { ...ticket, assignedTo, lastActivity: new Date().toISOString() }
            : ticket
        )
      );
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return {
    tickets,
    loading,
    error,
    pagination,
    fetchTickets,
    fetchTicketDetails,
    respondToTicket: respondToTicketAction,
    updateTicketStatus: updateTicketStatusAction,
    assignTicket: assignTicketAction
  };
};

export const useSupportStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async (period = 30) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getDashboardStats(period);
      setStats(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats
  };
};

export const useUnassignedTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUnassignedTickets = useCallback(async (limit = 50) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getUnassignedTickets(limit);
      setTickets(response.tickets || []);
    } catch (err) {
      setError(err.message);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnassignedTickets();
  }, [fetchUnassignedTickets]);

  return {
    tickets,
    loading,
    error,
    refetch: fetchUnassignedTickets
  };
};

export const useMyAssignedTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMyAssignedTickets = useCallback(async (limit = 50) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getMyAssignedTickets(limit);
      setTickets(response.tickets || []);
    } catch (err) {
      setError(err.message);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyAssignedTickets();
  }, [fetchMyAssignedTickets]);

  return {
    tickets,
    loading,
    error,
    refetch: fetchMyAssignedTickets
  };
};

export default useSupportTickets;
